import { action, observable } from 'mobx';
import { GetUserVisibleNonConfidentialNonNullFieldsForDataTypeService } from '../services/GetNonNullFieldsForDataTypeService';
import { ExecutorRoleViewAuthorizationService } from '../v2/api/authorization/service';
import { Hasher } from '../v2/api/hasher';
import { HasDependencies, hashString } from '../v2/api/types';
import { Dashboards } from '../v2/common/components/dashboards/types';
import { Domains, JapaneseEnterpriseClientDomains } from '../v2/constants';
import {
  AllUsedFields,
  DataFieldWithDataType,
  DataTypes,
  EmployeeDataFields as EDF,
} from '../v2/types';
import {
  getDataViewFieldsFromSettings,
  isHiddenField,
} from './../company-data-view-settings';

enum DateRangeOptions {
  DEFAULT = 'default',
  DATESHORT = 'mmmYY',
}

export class ReportStore implements HasDependencies {
  @observable
  public reportFieldsOrder: DataFieldWithDataType[] = [];

  @observable
  public isTimeSliderUpdated = false;

  @observable
  public dataViewFields: { [key in Dashboards]?: Set<DataFieldWithDataType> } =
    {};

  @observable
  public selectedDateRange: Record<DateRangeOptions, boolean> = {
    [DateRangeOptions.DEFAULT]: true,
    [DateRangeOptions.DATESHORT]: false,
  };

  constructor(
    readonly authorizationService: ExecutorRoleViewAuthorizationService,
    readonly allUsedFields: AllUsedFields
  ) {}

  public getDeps = () => [
    hashString([
      ...this.authorizationService.getDeps(),
      Hasher.hash(this.allUsedFields),
    ]),
  ];

  // Can be used to extend column selector with additional data types instead of only allowing to manipulate EMPLOYEE there
  public dataViewAdditionalDataTypesMap: Partial<
    Record<Dashboards, DataTypes[]>
  > = {
    [Dashboards.PAYROLL]: [DataTypes.PAYROLL],
    [Dashboards.SURVEY]: [DataTypes.QUESTIONANSWER],
  };

  private defaultFields = new Set([
    { dataType: DataTypes.EMPLOYEE, dataField: EDF.EMPLOYEE_ID },
    { dataType: DataTypes.EMPLOYEE, dataField: EDF.FULL_NAME },
    { dataType: DataTypes.EMPLOYEE, dataField: EDF.ORGANIZATION_LEVEL_1 },
    { dataType: DataTypes.EMPLOYEE, dataField: EDF.START_DATE },
    { dataType: DataTypes.EMPLOYEE, dataField: EDF.TENURE_GROUP },
    {
      dataType: DataTypes.EMPLOYEE,
      dataField: EDF.NATIONALITY_HIERARCHICAL_LEVEL_1,
    },
    { dataType: DataTypes.EMPLOYEE, dataField: EDF.AGE_GROUP },
  ]);

  @action
  public getSelectedDateRange = (): DateRangeOptions => {
    return Object.keys(this.selectedDateRange)
      .filter((k) => this.selectedDateRange[k as DateRangeOptions])
      .shift() as DateRangeOptions;
  };

  @action
  public setIsTimeSliderUpdated = (isTimeSliderUpdated: boolean) => {
    this.isTimeSliderUpdated = isTimeSliderUpdated;
  };

  @action
  public setInitialDataViewFields = (
    dashboard: Dashboards,
    domain: Domains,
    isUsingEffectiveLeaverDate: boolean
  ) => {
    const fields = this.dataViewFields[dashboard];
    if (fields && fields.size > 0) {
      return;
    }
    const nonNullFields =
      GetUserVisibleNonConfidentialNonNullFieldsForDataTypeService(
        DataTypes.EMPLOYEE,
        this.authorizationService,
        this.allUsedFields
      );
    const clonedNonNullFields = [...nonNullFields];
    const additionalNonNullDataTypes =
      this.dataViewAdditionalDataTypesMap[dashboard];
    if (additionalNonNullDataTypes) {
      for (const dataType of additionalNonNullDataTypes) {
        const dataFields =
          GetUserVisibleNonConfidentialNonNullFieldsForDataTypeService(
            dataType,
            this.authorizationService,
            this.allUsedFields
          );
        clonedNonNullFields.push(...dataFields);
      }
    }
    const selectedFields: DataFieldWithDataType[] =
      getDataViewFieldsFromSettings({
        domain,
        location: dashboard,
        defaultFields: this.defaultFields,
        nonNullFields: new Set(clonedNonNullFields),
        isJapaneseClient: JapaneseEnterpriseClientDomains.includes(domain),
        isUsingEffectiveLeaverDate,
      });
    this.dataViewFields[dashboard] = new Set(selectedFields);
  };

  @action
  public setDataViewFields = (
    dashboard: Dashboards,
    fields: DataFieldWithDataType[],
    domain: Domains
  ) => {
    this.dataViewFields[dashboard] = new Set(
      fields.filter((f) => !isHiddenField(f, domain))
    );
  };

  public getDataViewFields = (
    dashboard: Dashboards
  ): DataFieldWithDataType[] => {
    return Array.from(this.dataViewFields[dashboard] ?? this.defaultFields);
  };
}
